import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { resetFiltrosLocales } from 'slices/filterSlice'
import { aplicarFiltrosGenerico } from '../../helpers/filtros/aplicarFiltrosGenerico'
import { MAPA_NOTIFICACIONES } from '../../helpers/filtros/mapaNotificaciones.js'
import { obtenerCategoria } from '../../helpers/notificaciones/categoriasNotificacion.js'
import { obtenerReferencia, obtenerURL } from '../../helpers/notificaciones/rutasNotificacion.js'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth'
import { usePaginacion } from '../hooks/usePaginacion'
import { FiltrosContainer } from '../reutilizables/filtros/FiltrosContainer.jsx'
import { Paginacion } from '../reutilizables/paginacion/Paginacion.jsx'
import * as notificacionesService from '../servicios/notificacionesService'
import BordePrincipal from '../ui/BordePrincipal'
import LinkButton from '../ui/LinkButton'
import styles from './Notificaciones.module.css'

const CANT_MAX_NOTIFICACIONES = 99
const ITEMS_POR_PAGINA = 15

const Notificaciones = () => {
  const [notificaciones, setNotificaciones] = useState([])
  const [cargando, setCargando] = useState(true)
  const [textoInput, setTextoInput] = useState('')
  const [textoBusqueda, setTextoBusqueda] = useState('')
  const [soloNoLeidas, setSoloNoLeidas] = useState(false)
  const [totalRegistros, setTotalRegistros] = useState(0)
  const [totalNoLeidas, setTotalNoLeidas] = useState(0)
  const { filtrosLocales } = useSelector((state) => state.filters)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loginDataContext: { idUsuario } = {} } = useAuth()

  const cantidadNoLeidas = totalNoLeidas
  const hayNoLeidas = cantidadNoLeidas > 0

  const notificacionesFiltradas = useMemo(() => {
    const filtradas = aplicarFiltrosGenerico(
      notificaciones,
      { ...filtrosLocales, textoBusqueda, idUsuario },
      MAPA_NOTIFICACIONES
    )
    return soloNoLeidas ? filtradas.filter((n) => n.fechaLeido === null) : filtradas
  }, [notificaciones, filtrosLocales, textoBusqueda, idUsuario, soloNoLeidas])

  const {
    itemsActual: notificacionesPagina,
    paginaActual,
    setPaginaActual,
    itemsPorPagina,
    setItemsPorPagina,
    totalPaginas
  } = usePaginacion(notificacionesFiltradas, ITEMS_POR_PAGINA)

  const contextoFiltros = useMemo(
    () => ({ notificaciones, idUsuario }),
    [notificaciones, idUsuario]
  )

  const hayNoLeidasFiltradas = useMemo(
    () => notificacionesFiltradas.some((n) => n.fechaLeido === null),
    [notificacionesFiltradas]
  )

  const sincronizarNotificaciones = async () => {
    const { data } = await notificacionesService.buscarPorMensaje(idUsuario)
    setNotificaciones(Array.isArray(data?.notificaciones) ? data.notificaciones : [])
    setTotalRegistros(data?.totalRegistros ?? 0)
    setTotalNoLeidas(data?.totalNoLeidas ?? 0)
  }

  const fetchNotificaciones = async () => {
    setCargando(true)
    try {
      await sincronizarNotificaciones()
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al obtener las notificaciones.', 'notificacion')
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => {
    fetchNotificaciones()
  }, [])

  useEffect(() => {
    setPaginaActual(1)
  }, [notificacionesFiltradas.length])

  useEffect(() => {
    dispatch(resetFiltrosLocales())
    return () => {
      dispatch(resetFiltrosLocales())
    }
  }, [dispatch])

  const alternarLeidoLocal = (idMensaje, estabaNoLeida) => {
    setNotificaciones((prev) =>
      prev.map((n) =>
        n.idMensaje === idMensaje ? { ...n, fechaLeido: n.fechaLeido === null ? 'leido' : null } : n
      )
    )
    setTotalNoLeidas((prev) => Math.max(0, prev + (estabaNoLeida ? -1 : 1)))
  }

  const handleToggleLeido = async (e, idMensaje) => {
    e.preventDefault()
    e.stopPropagation()
    const estabaNoLeida = notificaciones.find((n) => n.idMensaje === idMensaje)?.fechaLeido === null
    alternarLeidoLocal(idMensaje, estabaNoLeida)
    try {
      await notificacionesService.actualizarNotificacion({ idMensaje })
    } catch (error) {
      alternarLeidoLocal(idMensaje, !estabaNoLeida)
      toastErrorLog(error, 'Ocurrió un error al actualizar la notificación.', 'notificacion')
    }
  }

  const handleClickNotificacion = async (idMensaje) => {
    const notificacion = notificaciones.find((n) => n.idMensaje === idMensaje)
    if (!notificacion || notificacion.fechaLeido !== null) return
    alternarLeidoLocal(idMensaje, true)
    try {
      await notificacionesService.actualizarNotificacion({ idMensaje })
    } catch (error) {
      alternarLeidoLocal(idMensaje, false)
      toastErrorLog(error, 'Ocurrió un error al actualizar la notificación.', 'notificacion')
    }
  }

  const handleMarcarTodas = async () => {
    if (notificacionesFiltradas.length === 0) return

    const marcarComoLeido = notificacionesFiltradas.some((n) => n.fechaLeido === null)
    const idsAfectados = notificacionesFiltradas
      .filter((n) => (n.fechaLeido === null) === marcarComoLeido)
      .map((n) => n.idMensaje)
    if (idsAfectados.length === 0) return

    const setAfectados = new Set(idsAfectados)
    const hayFiltroActivo = notificacionesFiltradas.length !== notificaciones.length
    const notificacionesPrevias = notificaciones
    const totalNoLeidasPrevio = totalNoLeidas

    setNotificaciones((prev) =>
      prev.map((n) =>
        setAfectados.has(n.idMensaje) ? { ...n, fechaLeido: marcarComoLeido ? 'leido' : null } : n
      )
    )
    setTotalNoLeidas(
      hayFiltroActivo
        ? Math.max(
            0,
            totalNoLeidasPrevio + (marcarComoLeido ? -idsAfectados.length : idsAfectados.length)
          )
        : marcarComoLeido
          ? 0
          : totalRegistros
    )

    try {
      await notificacionesService.marcarTodas(
        idUsuario,
        marcarComoLeido,
        hayFiltroActivo ? idsAfectados : null
      )
    } catch (error) {
      setNotificaciones(notificacionesPrevias)
      setTotalNoLeidas(totalNoLeidasPrevio)
      toastErrorLog(error, 'Ocurrió un error al actualizar las notificaciones.', 'notificacion')
      return
    }
    try {
      await sincronizarNotificaciones()
    } catch (error) {
      toastErrorLog(error, 'Hubo un error al sincronizar las notificaciones')
    }
  }

  return (
    <BordePrincipal titulo="Centro de notificaciones">
      <div className={styles.encabezado}>
        <div className={styles.titulo_grupo}>
          <span className="fa fa-bell" aria-hidden="true" />
          {hayNoLeidas && (
            <span className={styles.badge_no_leidas} aria-label={`${cantidadNoLeidas} sin leer`}>
              {cantidadNoLeidas > CANT_MAX_NOTIFICACIONES
                ? `${CANT_MAX_NOTIFICACIONES}+`
                : cantidadNoLeidas}
            </span>
          )}
        </div>
        <div className={styles.acciones}>
          <button
            type="button"
            className={`${styles.boton_marcar_todas} ${soloNoLeidas ? styles.boton_activo : ''}`}
            onClick={() => setSoloNoLeidas((prev) => !prev)}
            aria-pressed={soloNoLeidas}
            title={soloNoLeidas ? 'Ver todas las notificaciones' : 'Ver solo no leídas'}
          >
            <span className={`fa ${soloNoLeidas ? 'fa-list' : 'fa-filter'}`} aria-hidden="true" />
            {soloNoLeidas ? 'Ver todas' : 'Ver solo no leídas'}
          </button>
          <button
            type="button"
            className={styles.boton_marcar_todas}
            onClick={handleMarcarTodas}
            disabled={notificacionesFiltradas.length === 0}
            title={
              hayNoLeidasFiltradas ? 'Marcar todas como leídas' : 'Marcar todas como no leídas'
            }
          >
            <span
              className={`fa ${hayNoLeidasFiltradas ? 'fa-check-double' : 'fa-envelope'}`}
              aria-hidden="true"
            />
            {hayNoLeidasFiltradas ? 'Marcar todas como leídas' : 'Marcar todas como no leídas'}
          </button>
        </div>
      </div>

      <div className={styles.subtitulo}>
        {cargando
          ? 'Cargando notificaciones…'
          : hayNoLeidas
            ? `Tienes ${cantidadNoLeidas} ${cantidadNoLeidas === 1 ? 'notificación' : 'notificaciones'} sin leer`
            : 'Estás al día'}
      </div>

      <div className={styles.layout}>
        <FiltrosContainer
          arrayAFiltrar={notificaciones}
          mapaFiltros={MAPA_NOTIFICACIONES}
          contextoFiltros={contextoFiltros}
          filtrosVisibles={['categoriaNotificacion']}
          buscador={{
            onChange: setTextoBusqueda,
            placeholder: MAPA_NOTIFICACIONES.textoBusqueda.placeholder,
            valor: textoInput,
            setBusqueda: setTextoInput
          }}
        />

        <div className={styles.lista_columna}>
          <div className={styles.lista}>
            {cargando ? null : totalRegistros === 0 ? (
              <div className={styles.vacio}>
                <span className="fa fa-bell-slash" aria-hidden="true" />
                <p>Aún no tienes notificaciones.</p>
              </div>
            ) : notificacionesFiltradas.length === 0 ? (
              <div className={styles.vacio}>
                <span className="fa fa-filter" aria-hidden="true" />
                <p>No hay notificaciones que coincidan con el filtro.</p>
              </div>
            ) : (
              notificacionesPagina.map((notificacion) => {
                const referencia = obtenerReferencia(notificacion)
                const categoria = obtenerCategoria(notificacion, idUsuario)
                const url = obtenerURL(notificacion, idUsuario)
                const noLeida = notificacion.fechaLeido === null
                return (
                  <LinkButton
                    to={url}
                    key={notificacion.idMensaje}
                    onClick={() => {
                      handleClickNotificacion(notificacion.idMensaje)
                      if (!url) {
                        toastErrorLog(
                          null,
                          'Hubo un error al cargar la información.',
                          'notificacion'
                        )
                        navigate('/Notificaciones')
                      }
                    }}
                    className={`${styles.item} ${noLeida ? styles.item_no_leido : ''}`}
                  >
                    <span
                      role="button"
                      title={noLeida ? 'Marcar como leída' : 'Marcar como no leída'}
                      onClick={(e) => handleToggleLeido(e, notificacion.idMensaje)}
                      className={`fa fa-circle ${styles.circulo} ${noLeida ? styles.circulo_no_leido : styles.circulo_leido}`}
                    />
                    <div className={`${styles.contenido} ${noLeida ? styles.texto_no_leido : ''}`}>
                      <span className={`${styles.tag} ${styles[categoria.clase]}`}>
                        <span className={`fa ${categoria.icono}`} aria-hidden="true" />
                        {categoria.etiqueta}
                      </span>
                      <span className={styles.encabezado_notificacion}>
                        {notificacion.mensajeSistema}
                        {referencia && (
                          <>
                            {` ${referencia.etiqueta} `}
                            <b>#{referencia.id}</b>
                          </>
                        )}
                      </span>
                      {notificacion.titulo && (
                        <b className={styles.titulo_notificacion}>{notificacion.titulo}</b>
                      )}
                      {notificacion.texto && (
                        <div className={styles.texto_notificacion}>{notificacion.texto}</div>
                      )}
                    </div>
                  </LinkButton>
                )
              })
            )}
          </div>

          {!cargando && totalRegistros > 0 && (
            <Paginacion
              totalPaginas={totalPaginas}
              paginaActual={paginaActual}
              onCambioPagina={setPaginaActual}
              itemsPorPagina={itemsPorPagina}
              onCambioItemsPorPagina={(cantidad) => {
                setItemsPorPagina(cantidad)
                setPaginaActual(1)
              }}
            />
          )}
        </div>
      </div>
    </BordePrincipal>
  )
}

export default Notificaciones
